import React, { FC, useCallback, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';

import {
  RouteComponentProps,
  Link as RouterLink,
  navigate,
} from '@reach/router';
import { useEthers } from '@usedapp/core';

import Alert from '@bitcoin-portal/verse-web-components/dist/Alert';
import AmountFiat from '@bitcoin-portal/verse-web-components/dist/AmountFiat';
import Body from '@bitcoin-portal/verse-web-components/dist/Body';
import Button from '@bitcoin-portal/verse-web-components/dist/Button';
import { Card } from '@bitcoin-portal/verse-web-components/dist/Card';
import Coin from '@bitcoin-portal/verse-web-components/dist/Coin';
import CoinPair from '@bitcoin-portal/verse-web-components/dist/CoinPair';
import H2 from '@bitcoin-portal/verse-web-components/dist/H2';
import External from '@bitcoin-portal/verse-web-components/dist/Icons/External';
import Label from '@bitcoin-portal/verse-web-components/dist/Label';
import Link from '@bitcoin-portal/verse-web-components/dist/Link';
import Title from '@bitcoin-portal/verse-web-components/dist/Title';
import { colors } from '@bitcoin-portal/verse-web-components/dist/themes/colors';

import { NULL_PAIR, PRECISION } from '@context/constants';
import { useFarms } from '@context/providers/FarmsProvider';
import { useLiquidityPairs } from '@context/providers/LiquidityPairsProvider';
import { useTrackedState } from '@context/store';

import { lpTokenValue } from '@helpers/lpTokenValue';
import { findExternalIcon, findProvider, toFixed } from '@helpers/utils';

import { getMatchingPair } from '@views/Pools/helpers/getMatchingPair';

import {
  CoinWrapper,
  DetailHeader,
  DetailsWrapper,
  ExpiredFarmSummary,
  LabelWrapper,
  StyledChevron,
} from './styled';

interface ExpiredFarmProps extends RouteComponentProps {
  stakedBalance: number;
}

export const ExpiredFarm: FC<ExpiredFarmProps> = ({ stakedBalance }) => {
  const { provider: exchangeProvider } = useTrackedState();

  const { selectedFarm } = useFarms();
  const { liquidityPairs } = useLiquidityPairs();

  const { account } = useEthers();

  const lpTokenExplorerLink = (u: string, token: string): string => {
    return `https://etherscan.io/token/${token}?a=${u}`;
  };

  const provider = findProvider(exchangeProvider);

  const tickerA = useMemo(
    () => selectedFarm.pair?.token0?.symbol || '',
    [selectedFarm],
  );
  const tickerB = useMemo(
    () => selectedFarm.pair?.token1?.symbol || '',
    [selectedFarm],
  );

  const chainParam = useMemo(() => {
    return provider.chainParam || 'eth';
  }, [provider]);

  const pairParam = useMemo(() => {
    return `${tickerA.toLowerCase()}-${tickerB.toLowerCase()}`;
  }, [tickerA, tickerB]);

  const goBack = useCallback(() => {
    navigate(`/farms/${chainParam}/`);
  }, [chainParam]);

  const matchingPair = getMatchingPair(
    selectedFarm?.pair?.token0?.symbol,
    selectedFarm?.pair?.token1?.symbol,
    liquidityPairs ?? [],
  );

  const stakedBalanceFiat = lpTokenValue(
    stakedBalance,
    matchingPair ?? NULL_PAIR,
  );

  return (
    <>
      <DetailsWrapper>
        <DetailHeader>
          <StyledChevron onClick={goBack} />
          <CoinPair
            coinA={{
              ticker: tickerA,
              altSrc: findExternalIcon(
                selectedFarm.pair?.token0?.id,
                tickerA,
                exchangeProvider,
              ),
              size: '44px',
            }}
            coinB={{
              ticker: tickerB,
              altSrc: findExternalIcon(
                selectedFarm.pair?.token1?.id,
                tickerB,
                exchangeProvider,
              ),
              size: '44px',
            }}
          />
          <H2 style={{ padding: '16px 0 8px' }}>
            {tickerA}/{tickerB}
          </H2>
        </DetailHeader>
        <ExpiredFarmSummary>
          <Card>
            <LabelWrapper>
              <Label style={{ color: colors.shade80 }}>
                <FormattedMessage id="farms.expiredFarm.deposits" />
              </Label>
            </LabelWrapper>
            <H2>
              <AmountFiat
                amount={stakedBalanceFiat}
                currency="USD"
                locale="en-US"
              />
            </H2>
            <Link
              newTab
              href={lpTokenExplorerLink(
                account ?? '',
                selectedFarm?.stakingToken?.token ?? '',
              )}
            >
              <Body size="small" style={{ color: colors.shade90 }}>
                {toFixed(stakedBalance, PRECISION)} VERSE-X ({tickerA}/{tickerB}
                ) <External size="10px" />
              </Body>
            </Link>
          </Card>
          <Card>
            <LabelWrapper>
              <Label style={{ color: colors.shade80 }}>
                <FormattedMessage id="farms.expiredFarm.rewards" />
              </Label>
            </LabelWrapper>
            <CoinWrapper>
              <Coin
                alt={selectedFarm?.rewardToken?.ticker || 'verse'}
                altSrc={selectedFarm?.rewardToken?.icon}
                ticker={selectedFarm?.rewardToken?.ticker || 'verse'}
                size="24"
              />
              <Title>{selectedFarm?.rewardToken?.abbr || 'VERSE'}</Title>
            </CoinWrapper>
          </Card>
        </ExpiredFarmSummary>
        <div
          style={{
            marginBottom: 16,
            marginTop: 2,
          }}
        >
          <Alert
            design="warning"
            style={{ borderTopLeftRadius: 0, borderTopRightRadius: 0 }}
          >
            <Body
              size="medium"
              style={{
                color: '#d89c55',
                maxWidth: 410,
              }}
            >
              <FormattedMessage
                id="farms.expiredFarm.message"
                values={{
                  PAIR: `${tickerA}/${tickerB}`,
                }}
              />
            </Body>
          </Alert>
        </div>
        <RouterLink to={`/farms/${chainParam}/${pairParam}/withdraw`}>
          <Button small fullWidth as="div" design="secondary">
            <FormattedMessage
              id="farms.expiredFarm.button"
              values={{
                PAIR: `${tickerA}/${tickerB}`,
              }}
            />
          </Button>
        </RouterLink>
      </DetailsWrapper>
    </>
  );
};

export default ExpiredFarm;
